import { supabase } from '../supabase';

export const getQuestions = async ({ sortBy = 'newest', search = '', page = 1, limit = 10 }) => {
  try {
    let query = supabase
      .from('questions')
      .select(`
        *,
        author:users(id, username, avatar_url),
        answers:answers(count),
        tags:question_tags(tags(*))
      `)
      .range((page - 1) * limit, page * limit - 1);

    // Add search condition if provided
    if (search) {
      query = query.or(`title.ilike.%${search}%,content.ilike.%${search}%`);
    }

    // Add sorting
    switch (sortBy) {
      case 'newest':
        query = query.order('created_at', { ascending: false });
        break;
      case 'popular':
        query = query.order('views_count', { ascending: false });
        break;
      case 'unanswered':
        query = query.eq('answers.count', 0);
        break;
      default:
        query = query.order('created_at', { ascending: false });
    }

    const { data, error } = await query;

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching questions:', error);
    throw error;
  }
};

export const getQuestionById = async (id) => {
  try {
    const { data, error } = await supabase
      .from('questions')
      .select(`
        *,
        author:users(id, username, avatar_url),
        answers(
          *,
          author:users(id, username, avatar_url),
          votes(vote_type)
        ),
        tags:question_tags(tags(*)),
        votes(vote_type)
      `)
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching question:', error);
    throw error;
  }
};

export const createQuestion = async (questionData) => {
  try {
    const { data, error } = await supabase
      .from('question2')
      .insert([questionData])
      .select()
      .single();

    if (error) {
      console.error('Supabase error:', error);
      throw new Error(error.message);
    }

    return data;
  } catch (error) {
    console.error('Error in createQuestion:', error);
    throw error;
  }
};

export const createAnswer = async (questionId, answerData) => {
  try {
    const response = await supabase
      .from('answers')
      .insert({
        content: answerData.content,
        author_id: supabase.auth.user().id,
        question_id: questionId
      })
      .single();
    return response;
  } catch (error) {
    console.error('Error creating answer:', error);
    throw error;
  }
};

export const voteQuestion = async (questionId, voteType) => {
  try {
    const user = supabase.auth.getUser();
    
    const { data: existingVote } = await supabase
      .from('votes')
      .select('*')
      .eq('user_id', user.id)
      .eq('target_type', 'question')
      .eq('target_id', questionId)
      .single();

    if (existingVote) {
      if (existingVote.vote_type === voteType) {
        // Remove vote if clicking same button
        const { error } = await supabase
          .from('votes')
          .delete()
          .match({ id: existingVote.id });
        
        if (error) throw error;
      } else {
        // Update vote if changing vote type
        const { error } = await supabase
          .from('votes')
          .update({ vote_type: voteType })
          .match({ id: existingVote.id });
        
        if (error) throw error;
      }
    } else {
      // Create new vote
      const { error } = await supabase
        .from('votes')
        .insert({
          user_id: user.id,
          target_type: 'question',
          target_id: questionId,
          vote_type: voteType
        });
      
      if (error) throw error;
    }

    return true;
  } catch (error) {
    console.error('Error voting:', error);
    throw error;
  }
};

export const voteAnswer = async (answerId, voteType) => {
  try {
    const response = await supabase
      .from('votes')
      .update({ vote_type: voteType })
      .eq('target_type', 'answer')
      .eq('target_id', answerId);

    if (response.error) throw response.error;
    return true;
  } catch (error) {
    console.error('Error voting:', error);
    throw error;
  }
};

// Run this in browser console to debug
const checkAuth = async () => {
  const { data } = await supabase.auth.getSession();
  console.log('Current session:', data);
};
checkAuth(); 